import i18next from 'i18next';

export interface SeverityMapping {
  mild: string | number;
  moderate: string | number;
  severe: string | number;
}

export const SEVERITY_VALUES: SeverityMapping = {
  mild: 30,
  moderate: 40,
  severe: 80,
};

export interface SeverityLabels {
  high: string;
  medium: string;
  low: string;
}

export interface SeverityLevelResult {
  label?: string;
  class: string;
  color: string;
  restColor: string;
}

export function getSeverityLevelByValue(
  value: number,
  options?: {
    labels?: SeverityLabels;
    invertColors?: boolean;
  }
): SeverityLevelResult {
  const { labels, invertColors = false } = options || {};

  if (invertColors) {
    if (value >= 75) {
      return {
        ...(labels && { label: labels.low }),
        class: 'severity-low',
        color: 'var(--signal-low)',
        restColor: 'var(--signal-low-rest)',
      };
    } else if (value >= 50) {
      return {
        ...(labels && { label: labels.medium }),
        class: 'severity-moderate',
        color: 'var(--signal-medium)',
        restColor: 'var(--signal-medium-rest)',
      };
    } else {
      return {
        ...(labels && { label: labels.high }),
        class: 'severity-high',
        color: 'var(--signal-high)',
        restColor: 'var(--signal-high-rest)',
      };
    }
  } else {
    if (value >= 75) {
      return {
        ...(labels && { label: labels.high }),
        class: 'severity-high',
        color: 'var(--signal-high)',
        restColor: 'var(--signal-high-rest)',
      };
    } else if (value >= 50) {
      return {
        ...(labels && { label: labels.medium }),
        class: 'severity-moderate',
        color: 'var(--signal-medium)',
        restColor: 'var(--signal-medium-rest)',
      };
    } else {
      return {
        ...(labels && { label: labels.low }),
        class: 'severity-low',
        color: 'var(--signal-low)',
        restColor: 'var(--signal-low-rest)',
      };
    }
  }
}

export const CIRCLE_CIRCUMFERENCE = 263.89;

export function transformName(value: string): string {
  if (!value) return '';

  const typeTranslationMap: { [key: string]: string } = {
    eye_analysis: 'results.biomarkerCard.types.eye_analysis',
    skin_analysis: 'results.biomarkerCard.types.skin_analysis',
  };

  if (typeTranslationMap[value]) {
    return i18next.t(typeTranslationMap[value]);
  }

  return value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function transformSeverityForDisplay(severity: string): string {
  if (severity.startsWith('results.biomarkers.')) {
    return i18next.t(severity);
  }

  const severityLower = severity.toLowerCase();

  const severityTranslationMap: { [key: string]: string } = {
    severe: 'results.biomarkerCard.severity.high',
    moderate: 'results.biomarkerCard.severity.elevated',
    mild: 'results.biomarkerCard.severity.mild',
  };

  if (severityTranslationMap[severityLower]) {
    return i18next.t(severityTranslationMap[severityLower]);
  }

  return severity;
}

export function getSeverityColor(severity: string): string {
  let severityKey = severity.toLowerCase();
  
  if (severity.includes('results.biomarkers.severity.')) {
    severityKey = severity.split('.').pop()?.toLowerCase() || 'mild';
  }

  const computedStyle = getComputedStyle(document.documentElement);

  const colorMap: { [key: string]: string } = {
    mild: computedStyle.getPropertyValue('--severity-mild').trim() || '#e2a8be',
    moderate:
      computedStyle.getPropertyValue('--severity-moderate').trim() || '#ce99d9',
    severe:
      computedStyle.getPropertyValue('--severity-severe').trim() || '#a450bd',
  };

  return colorMap[severityKey] || colorMap['mild'];
}

export function getSeverityValue(severity: string): number {
  let severityKey = severity.toLowerCase();
  
  if (severity.includes('results.biomarkers.severity.')) {
    severityKey = severity.split('.').pop()?.toLowerCase() || 'mild';
  }

  return (SEVERITY_VALUES as any)[severityKey] || 0;
}

export function getSeverityDashOffset(severity: string): number {
  const percentage = getSeverityValue(severity);
  return CIRCLE_CIRCUMFERENCE - (CIRCLE_CIRCUMFERENCE * percentage) / 100;
}

export function getStrokeDashOffset(percentage: number): number {
  return CIRCLE_CIRCUMFERENCE - (CIRCLE_CIRCUMFERENCE * percentage) / 100;
}


